import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { IconLocationPinTarget } from '@toolshed/styleguide/components/AppIcon/AppIcons';
import { LocationIcon } from 'components/Search/LocationField';
import dynamic from 'next/dynamic';
import useFunnelling from 'components/Funnelling/hooks/useFunnelling';

import { IconFilterList, Modal } from 'lib/design-system';
import {
  clearFlow,
  FlowId,
  startFlow,
  updateFlow,
  stopFlow,
  track,
} from 'lib/sdks/analytics';
import { FunnellingFlowStepIds } from 'components/Funnelling/api';
import { AnchorForm, Content } from './style';
import { getTrackingPropsFromFunnelling } from 'components/Funnelling/utils/tracking';
import { spacing1 } from 'lib/mortar';
import { useOptimizely, Experiment } from 'lib/sdks/optimizely';
import { Button } from '@checkatrade/web-ui';
import { colors } from '@checkatrade/web-ui/themes';
import type { APIFunnelJourney } from 'lib/sdks/strapi';
import useIsMobileScreen from '@hooks/use-is-mobile-screen';

const SearchForm = dynamic(() => import('components/Search/SearchForm'));
const Funnelling = dynamic(() => import('components/Funnelling/Funnelling'));
const SemanticFlowSwitch = dynamic(
  () =>
    import('components/@pages/HomePage/SemanticSearchModal/SemanticFlowSwitch'),
);

type SearchDockProps = {
  onSearchSubmit: (args: any) => void;
  categoryId: number;
  location: string;
  categoryLabel: string;
  categories: any[];
  handleCategorySearch: (category: string) => void;
  onShowRefineCategories: () => void;
  journey?: APIFunnelJourney;
  showFilterButton: boolean;
  hasFunnel: boolean;
};

export const SearchDock = ({
  categoryId,
  location = '',
  onSearchSubmit,
  categoryLabel = '',
  categories,
  handleCategorySearch,
  journey,
  showFilterButton,
  onShowRefineCategories,
  hasFunnel = false,
}: SearchDockProps) => {
  const funnelling = useFunnelling();
  const [initialSearchBar, setInitialSearchBar] = useState(true);
  const [showSemanticSearchModal, setShowSemanticSearchModal] = useState(false);
  const [getLocationOnMount, setGetLocationOnMount] = useState(false);
  const [focusOnLocation, setFocusOnLocation] = useState(false);
  const [mobileSearchExpanded, setMobileSearchExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const isMobileScreen = useIsMobileScreen();

  const { decide, loading: optimizelyLoading } = useOptimizely();

  const semanticSearchVariant = useMemo(() => {
    if (optimizelyLoading) return false;
    const decision = decide(Experiment.SEMANTIC_SEARCH_VARIANTS);
    return decision?.enabled && decision?.variationKey !== 'control'
      ? decision?.variationKey
      : null;
  }, [optimizelyLoading, decide]);

  const openChange = useCallback(
    (value: boolean, option?: { focusOnLocation: boolean }) => {
      setInitialSearchBar(!value);
      setMobileSearchExpanded(true);
      setFocusOnLocation(!!option?.focusOnLocation);
      if (semanticSearchVariant && !option?.focusOnLocation && value) {
        setShowSemanticSearchModal(true);
      }
    },
    [semanticSearchVariant],
  );

  const onSearchButtonBlur = () => {
    setTimeout(() => {
      filterButtonRef.current?.focus();
    });
  };

  const close = useCallback(
    (e: MouseEvent | FocusEvent) => {
      if (
        ref.current &&
        !ref.current.contains(e.target as Node) &&
        // some child elements are removed from dom
        document.contains(e.target as Node)
      ) {
        openChange(false);
      }
    },
    [openChange],
  );

  useEffect(() => {
    if (initialSearchBar) return;
    const controller = new AbortController();
    document.addEventListener('click', close, { signal: controller.signal });
    document.addEventListener('focusin', close, { signal: controller.signal });

    return () => controller.abort();
  }, [close, initialSearchBar]);

  const onLocationIconClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      setGetLocationOnMount(true);
      openChange(true, { focusOnLocation: true });
    },
    [openChange],
  );

  const hasCategoryAndLocation = categoryLabel && location;
  const searchLabel = hasCategoryAndLocation
    ? `${categoryLabel} in ${location}`
    : 'Trade (e.g. electrician)';

  const handleSearchSubmit = async (formValues: any) => {
    const [isFunnelling, config] = await funnelling.initialise(
      formValues.categoryId,
      { location: formValues.location, type: 'Filtering' },
    );

    openChange(false);
    if (isFunnelling) {
      startFlow(FlowId.SEARCH_FUNNELLING, config.startingScreen, {
        ...getTrackingPropsFromFunnelling({
          categoryId: formValues.categoryId,
          funnelConfig: config,
        }),
        postcode: formValues.location,
        source: 'search',
      });
      return;
    }

    onSearchSubmit(formValues);
  };

  const handleFunnelSelection = (
    screen: number,
    choice: string,
    nextScreen?: number,
  ) => {
    updateFlow(
      FlowId.SEARCH_FUNNELLING,
      nextScreen || FunnellingFlowStepIds.POSTCODE,
      {
        ...getTrackingPropsFromFunnelling({
          ...funnelling,
          fieldValues: {
            ...funnelling.fieldValues,
            [screen]: choice,
          },
        }),
        source: 'search',
      },
    );
  };

  const handleFunnelComplete = ({
    categoryId: funnelledCategoryId,
    postcode,
  }: {
    categoryId: number | null;
    postcode: string;
  }) => {
    clearFlow(
      FlowId.SEARCH_FUNNELLING,
      {
        ...getTrackingPropsFromFunnelling(funnelling),
        postcode,
        source: 'search',
        category_id: funnelledCategoryId,
      },
      FunnellingFlowStepIds.CLEARED,
    );
    onSearchSubmit({ categoryId: funnelledCategoryId, location: postcode });
    funnelling.stop();
  };

  const handleOnBack = () => {
    openChange(false);
    if (semanticSearchVariant) {
      setShowSemanticSearchModal(true);
    }

    stopFlow(FlowId.SEARCH_FUNNELLING, {
      ...getTrackingPropsFromFunnelling(funnelling),
      source: 'search',
    });
    funnelling.stop();
  };

  const stopFunnel = () => {
    openChange(false);
    if (semanticSearchVariant) {
      setShowSemanticSearchModal(false);
    }

    stopFlow(FlowId.SEARCH_FUNNELLING, {
      ...getTrackingPropsFromFunnelling(funnelling),
      source: 'search',
    });
    funnelling.stop();
  };

  const handleSemanticSearchModal = () => {
    if (semanticSearchVariant) {
      setShowSemanticSearchModal(true);
    }
  };

  const onHandleCategorySubmit = async (newCategoryId: any) => {
    await handleSearchSubmit({ categoryId: newCategoryId, location });
    setShowSemanticSearchModal(false);
  };

  const onFilterClick = async () => {
    track('search.filter', { categoryId: categoryId, resource_type: 'search' });

    if (hasFunnel) {
      const [isFunnelling, config] = await funnelling.initialise(categoryId, {
        journey,
        type: 'Filtering',
      });
      if (isFunnelling) {
        startFlow(FlowId.SEARCH_FUNNELLING, config.startingScreen, {
          ...getTrackingPropsFromFunnelling({
            categoryId: categoryId,
            funnelConfig: config,
          }),
          postcode: location,
          source: 'search',
        });
      }
    } else {
      onShowRefineCategories();
    }

    // remove focus state when the modal open as the enter key trigger a new click event
    filterButtonRef?.current?.blur();
  };

  return (
    <>
      <div
        id="search-dock"
        data-guid="DockWrapper"
        ref={ref}
        className="flex w-full flex-col bg-primary p-0 md:h-[66px]"
      >
        <Content>
          {(isMobileScreen && !mobileSearchExpanded) ||
          (initialSearchBar && !semanticSearchVariant) ? (
            <>
              <AnchorForm as="form" onSubmit={() => openChange(true)}>
                <div
                  className="relative mr-2 grid w-full max-w-lg grid-cols-[1fr,auto,auto] gap-2 overflow-hidden rounded-full bg-main p-1"
                  id="search-anchor"
                  onClick={() => openChange(true)}
                  onFocus={() => openChange(true)}
                >
                  <input
                    className="sr-only flex-1"
                    aria-label={searchLabel}
                    title={searchLabel}
                  />
                  <div className="flex flex-1 flex-col overflow-hidden pl-4">
                    {hasCategoryAndLocation && (
                      <span className="text-xs text-neutral-strong">Find</span>
                    )}
                    <span className="min-w-0 truncate" aria-hidden="true">
                      {hasCategoryAndLocation ? (
                        <>
                          {categoryLabel} in{' '}
                          <span
                            aria-hidden="true"
                            onClick={event => {
                              event.stopPropagation();
                              openChange(true, { focusOnLocation: true });
                            }}
                          >
                            {location}
                          </span>
                        </>
                      ) : (
                        searchLabel
                      )}
                    </span>
                  </div>
                  <LocationIcon
                    autoComplete="postal-code"
                    tabIndex={-1}
                    onMouseDown={onLocationIconClick} //parent div onfocus event overrides onClick event
                  >
                    <span className="sr-only">Find my postcode</span>
                    <IconLocationPinTarget
                      size="medium"
                      color={colors.secondary.DEFAULT}
                      style={{
                        margin: `${spacing1} 0`,
                      }}
                    />
                  </LocationIcon>
                  <Button
                    className="px-6 md:min-w-36"
                    variant="secondary"
                    data-guid="SearchFormCta"
                    type="submit"
                  >
                    Search
                  </Button>
                </div>
              </AnchorForm>
              {!isMobileScreen && showFilterButton && (
                <Button
                  ref={filterButtonRef}
                  startContent={<IconFilterList size="small" />}
                  variant="outline"
                  onClick={onFilterClick}
                  aria-label="Filter search results"
                >
                  <span className="hidden md:inline">Filter</span>
                </Button>
              )}
            </>
          ) : (
            <SearchForm
              formId="search-form"
              onSubmit={handleSearchSubmit}
              categoryId={categoryId}
              location={location}
              categoryLabel={categoryLabel}
              categories={categories}
              handleCategorySearch={handleCategorySearch}
              onTrackEvent={args =>
                track(args.key, { ...args.data, resource_type: 'search' })
              }
              getLocationOnMount={getLocationOnMount}
              autoFocusOnCategory={!focusOnLocation}
              autoFocusOnLocation={focusOnLocation}
              handleSemanticSearchModal={handleSemanticSearchModal}
              semanticSearchVariant={semanticSearchVariant}
              shouldFixSearchButtonWidth
              onSearchButtonBlur={onSearchButtonBlur}
            />
          )}
        </Content>
      </div>
      {(funnelling.funnelConfig || showSemanticSearchModal) && (
        <Modal
          ariaLabelledBy="categoryFunnellingSelectHeading"
          size="medium"
          minHeight="65vh"
          fullScreenOnMobile
          onDismiss={stopFunnel}
        >
          {!showSemanticSearchModal && funnelling.funnelConfig && (
            <Funnelling
              onComplete={handleFunnelComplete}
              onBack={handleOnBack}
              onSelect={handleFunnelSelection}
              locationButtonText="Continue"
              {...funnelling}
            />
          )}
          {showSemanticSearchModal && (
            <SemanticFlowSwitch
              variant={semanticSearchVariant as string}
              showFlowSwitch={false}
              handleCategorySearchSubmit={onHandleCategorySubmit}
              handleCategorySearch={handleCategorySearch}
            />
          )}
        </Modal>
      )}
    </>
  );
};

export default SearchDock;
